import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import {
  MapPin,
  Star,
  Clock,
  Phone,
  Globe,
  ChevronRight,
  DollarSign,
  Trophy,
  ArrowRight,
  HelpCircle,
  ChevronDown,
  Info,
} from "lucide-react";
import { stateByCode, getStateUrl, getCityUrl, getCourseUrl, getListingForCity, getStateName, getCleanDescription, slugify } from "../constants";
import { getCourse, getCourses, getCourseSync, getCoursesSync } from "../api";
import { ListingCard } from "../components/CourseCard";
import FormattedText from "../components/FormattedText";
import SEO from "../components/SEO";
import NotFound from "./NotFound";
import { buildCourseSchema, buildFaqSchema, buildBreadcrumbSchema } from "../utils/seoSchemas";

const CoursePage = () => {
  const { stateCode, courseId } = useParams();
  const [course, setCourse] = useState(() => getCourseSync(courseId));
  const state = useMemo(() => {
    return (
      stateByCode(stateCode || course?.state) ||
      (course?.state ? { code: course.state.toLowerCase(), name: getStateName(course.state) } : null) || {
        code: "al",
        name: "Alabama",
      }
    );
  }, [stateCode, course?.state]);

  const isInvalidStateCode = useMemo(() => {
    if (stateCode && state) {
      return stateCode.toLowerCase() !== slugify(state.name);
    }
    return false;
  }, [stateCode, state]);

  const [nearby, setNearby] = useState(() => {
    const initialCourse = getCourseSync(courseId);
    const stCode = state?.code || stateCode || initialCourse?.state;
    const list = getCoursesSync({ state: stCode, limit: 12 });
    return initialCourse ? list.filter((x) => x.id !== initialCourse.id).slice(0, 6) : list.slice(0, 6);
  });
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    getCourse(courseId)
      .then((c) => {
        if (c) {
          setCourse(c);
          const stCode = state?.code || stateCode || c.state;
          getCourses({ state: stCode, limit: 12 })
            .then((cs) => setNearby(cs.filter((x) => x.id !== c.id).slice(0, 6)))
            .catch(() => {});
        }
      })
      .catch(() => {
        if (!getCourseSync(courseId)) {
          setCourse(null);
        }
      });
  }, [courseId, stateCode, state]);

  if (course === undefined) {
    return <div className="max-w-4xl mx-auto px-4 py-24 text-center text-gray-500" data-testid="course-loading">Loading...</div>;
  }

  if (!course || isInvalidStateCode) {
    return <NotFound />;
  }

  const websiteUrl =
    (course.website && course.website !== "https://example.com")
      ? course.website
      : "#";

  const cleanDescription = getCleanDescription(course);
  const descriptionParagraphs = cleanDescription
    .split(/\n\s*\n|\n/)
    .map((p) => p.trim())
    .filter(Boolean);

  // Dynamic FAQs tailored to this specific venue
  const faqs = [
    {
      q: `What are the operating hours for ${course.name}?`,
      a: `${course.name} is open throughout the week with regular hours: Monday–Thursday (${course.hours?.Monday || "10:00 AM - 9:00 PM"}), Friday (${course.hours?.Friday || "10:00 AM - 11:00 PM"}), Saturday (${course.hours?.Saturday || "9:00 AM - 11:00 PM"}), and Sunday (${course.hours?.Sunday || "9:00 AM - 8:00 PM"}). Hours may vary on holidays or due to weather.`,
    },
    {
      q: `How much does a round of mini golf cost at ${course.name}?`,
      a: `Pricing at ${course.name} is typically around ${course.priceRange || "$8 - $14 per round"}. Many venues offer group rates, student discounts, and family passes. Please check their website or call ahead for seasonal specials.`,
    },
    {
      q: `Where is ${course.name} located in ${course.city}?`,
      a: `${course.name} is located at ${course.address || `${course.city}, ${state.name}`}. You can contact their front desk directly at ${course.phone || "(555) 000-0000"} for directions, parking details, or reservations.`,
    },
    {
      q: `Can I host birthday parties or private group events at ${course.name}?`,
      a: `Yes, ${course.name} in ${course.city}, ${state.name} is a premier destination for birthday parties, school trips, corporate outings, and family gatherings. We recommend contacting them in advance to reserve party rooms and tee times.`,
    },
    {
      q: `What other mini golf courses are located near ${course.city}, ${state.name}?`,
      a: `There are multiple exciting mini golf and putt-putt destinations in ${course.city} and surrounding areas across ${state.name}. Check out the "More Mini Golf Near ${course.city}" section below or browse all ${state.name} listings in our directory.`,
    },
  ];

  const currentCourseUrl = `https://minigolfalabama.com${getCourseUrl(course)}`;
  const courseSchemas = [
    buildCourseSchema(course, state, currentCourseUrl),
    buildFaqSchema(faqs),
    buildBreadcrumbSchema([
      { name: "Home", url: "/" },
      { name: state.name, url: getStateUrl(state.code) },
      { name: course.city, url: getCityUrl(state.code, course.city) },
      { name: course.name, url: currentCourseUrl },
    ]),
  ].filter(Boolean);

  return (
    <div data-testid="course-page" className="bg-[#f8fafc]">
      <SEO
        title={`${course.name} - Mini Golf in ${course.city}, ${state.name}`}
        description={cleanDescription.slice(0, 160)}
        canonicalUrl={currentCourseUrl}
        ogType="business.business"
        ogImage={course.image}
        schema={courseSchemas}
      />
      {/* Hero image */}
      <div className="relative h-[340px] sm:h-[420px]">
        <img src={course.image} alt={course.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#111217] via-black/40 to-black/20" />
        <div className="absolute bottom-0 left-0 right-0">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
            <nav className="flex items-center gap-1.5 text-sm text-gray-300 mb-3">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <ChevronRight className="w-4 h-4 text-gray-400" />
              <Link to={getStateUrl(state.code)} className="hover:text-white transition-colors">
                {state.name}
              </Link>
              <ChevronRight className="w-4 h-4 text-gray-400" />
              <span className="text-[#e5243b] font-medium">{course.name}</span>
            </nav>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-3xl sm:text-5xl font-black text-white tracking-tight" data-testid="course-title">
                {course.name}
              </h1>
              {course.featured && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-[#e5243b] text-white text-xs font-bold rounded-full shadow">
                  <Trophy className="w-3.5 h-3.5" /> Featured
                </span>
              )}
            </div>
            <div className="flex items-center gap-4 mt-3 text-sm text-gray-200 flex-wrap">
              <span className="flex items-center gap-1.5">
                <Star className="w-4 h-4 text-[#f5cd79] fill-[#f5cd79]" />
                <strong className="text-white">{course.rating}</strong> ({course.reviewCount} verified reviews)
              </span>
              <span className="flex items-center gap-1.5">
                <MapPin className="w-4 h-4 text-[#e5243b]" /> {course.city}, {state.name}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-1 lg:grid-cols-3 gap-10 min-w-0">
        <div className="lg:col-span-2 space-y-8 min-w-0">
          {/* About Section Card */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 sm:p-8 shadow-sm overflow-hidden min-w-0">
            <h2 className="text-2xl font-black text-gray-900 tracking-tight mb-4">
              About {course.name}
            </h2>
            <div className="space-y-3.5 text-[15px] leading-relaxed text-gray-700 break-words overflow-hidden">
              <FormattedText
                text={cleanDescription}
                paragraphClassName="break-words leading-relaxed"
                linkClassName="text-emerald-600 hover:text-emerald-700 underline font-semibold transition-colors break-words"
              />
            </div>
            <div className="mt-6 pt-5 border-t border-gray-100 flex items-start gap-2.5 text-xs text-gray-500 leading-relaxed">
              <Info className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
              <span>
                Business information and photos provided by Google. The star rating and review count reflect aggregate Google data and may have changed — please confirm hours, pricing, and details with the venue before visiting.
              </span>
            </div>
          </div>

          {/* More Mini Golf Near [City] Card (Styled matching reference) */}
          <div className="bg-[#181a20] text-white border border-gray-800 rounded-2xl p-6 sm:p-8 shadow-lg">
            <h3 className="text-xl sm:text-2xl font-black text-white tracking-tight mb-5 flex items-center justify-between">
              <span>More Mini Golf Near {course.city}</span>
            </h3>

            <div className="divide-y divide-gray-800">
              {nearby.length === 0 ? (
                <div className="py-4 text-sm text-gray-400">
                  No other courses currently listed nearby. Check the state directory for all options.
                </div>
              ) : (
                nearby.map((c) => (
                  <Link
                    key={c.id}
                    to={getCourseUrl(c)}
                    className="flex items-center justify-between py-3.5 hover:bg-white/5 px-2 rounded-xl transition-all group"
                  >
                    <span className="font-semibold text-emerald-400 group-hover:text-emerald-300 transition-colors text-[15px]">
                      {c.name}
                    </span>
                    <span className="text-sm text-gray-300 flex items-center gap-1.5 flex-shrink-0">
                      <span>{c.city}</span>
                      <span>·</span>
                      <span className="text-amber-400 font-semibold">{c.rating}★</span>
                    </span>
                  </Link>
                ))
              )}
            </div>

            <div className="mt-5 pt-4 border-t border-gray-800">
              <Link
                to={getStateUrl(state.code)}
                className="text-sm font-bold text-emerald-400 hover:text-emerald-300 transition-colors inline-flex items-center gap-1.5 group"
              >
                <span>See all mini golf courses in {state.name}</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>

          {/* Visitor Rating Summary */}
          <div className="bg-white border border-gray-200 rounded-2xl p-6 sm:p-8 shadow-sm">
            <h2 className="text-xl sm:text-2xl font-black text-gray-900 tracking-tight mb-4">
              Visitor Rating & Reviews
            </h2>
            <div className="flex items-center gap-6">
              <div className="text-5xl font-black text-gray-900">{course.rating}</div>
              <div>
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star
                      key={i}
                      className={`w-5 h-5 ${
                        i <= Math.round(course.rating)
                          ? "text-[#f5cd79] fill-[#f5cd79]"
                          : "text-gray-300"
                      }`}
                    />
                  ))}
                </div>
                <p className="text-sm text-gray-600 mt-1.5 font-medium">
                  Based on {course.reviewCount} verified Google reviews
                </p>
              </div>
            </div>
          </div>

          {/* Dynamic FAQ Accordion Section */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 sm:p-8 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center">
                <HelpCircle className="w-6 h-6 text-[#e5243b]" />
              </div>
              <div>
                <h2 className="text-xl sm:text-2xl font-black text-gray-900 tracking-tight">
                  Frequently Asked Questions
                </h2>
                <p className="text-xs sm:text-sm text-gray-500">Everything you need to know before visiting {course.name}</p>
              </div>
            </div>

            <div className="space-y-3">
              {faqs.map((faq, idx) => {
                const isOpen = openFaq === idx;
                return (
                  <div
                    key={idx}
                    className="border border-gray-200 rounded-xl overflow-hidden transition-all duration-200"
                  >
                    <button
                      type="button"
                      onClick={() => setOpenFaq(isOpen ? -1 : idx)}
                      className="w-full flex items-center justify-between p-4 sm:p-5 text-left bg-gray-50/50 hover:bg-gray-100/70 transition-colors"
                    >
                      <span className="font-bold text-gray-900 text-sm sm:text-base pr-4">
                        {faq.q}
                      </span>
                      <ChevronDown
                        className={`w-5 h-5 text-gray-500 flex-shrink-0 transition-transform duration-200 ${
                          isOpen ? "rotate-180 text-[#e5243b]" : ""
                        }`}
                      />
                    </button>
                    {isOpen && (
                      <div className="p-4 sm:p-5 bg-white text-gray-700 text-sm leading-relaxed border-t border-gray-100">
                        {faq.a}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <aside>
          <div className="bg-white border border-gray-200 rounded-2xl p-6 sticky top-24 shadow-sm">
            <h3 className="text-lg font-bold text-gray-900 mb-5 border-l-2 border-[#e5243b] pl-2.5">Course Information</h3>
            <ul className="space-y-4 text-sm">
              <li className="flex gap-3">
                <MapPin className="w-5 h-5 text-[#e5243b] flex-shrink-0" />
                <div>
                  <div className="font-semibold text-gray-900">Address</div>
                  <div className="text-gray-600 mt-0.5">{course.address}</div>
                </div>
              </li>
              <li className="flex gap-3">
                <Phone className="w-5 h-5 text-[#2563eb] flex-shrink-0" />
                <div>
                  <div className="font-semibold text-gray-900">Phone</div>
                  <div className="text-gray-600 mt-0.5">{course.phone}</div>
                </div>
              </li>
              <li className="flex gap-3">
                <DollarSign className="w-5 h-5 text-[#10b981] flex-shrink-0" />
                <div>
                  <div className="font-semibold text-gray-900">Pricing</div>
                  <div className="text-gray-600 mt-0.5">{course.priceRange}</div>
                </div>
              </li>
              <li className="flex gap-3">
                <Globe className="w-5 h-5 text-[#3b82f6] flex-shrink-0" />
                <div>
                  <div className="font-semibold text-gray-900">Website</div>
                  <a
                    href={websiteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#2563eb] hover:text-[#1e40af] font-medium mt-0.5 inline-block transition-colors"
                  >
                    Visit official website
                  </a>
                </div>
              </li>
              <li className="flex gap-3">
                <Clock className="w-5 h-5 text-[#e5243b] flex-shrink-0" />
                <div className="flex-1">
                  <div className="font-semibold text-gray-900 mb-2">Hours</div>
                  <div className="space-y-1.5">
                    {Object.entries(course.hours || {}).map(([day, hrs]) => (
                      <div key={day} className="flex justify-between gap-4 text-xs">
                        <span className="text-gray-500">{day}</span>
                        <span className="text-gray-800 font-medium">{hrs}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </aside>
      </div>

      {/* Bottom More Courses in State */}
      {nearby.length > 0 && (
        <div className="bg-[#f1f5f9] py-14 border-t border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight mb-8">
              Explore More in {state.name}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {nearby.slice(0, 4).map((c) => (
                <ListingCard key={c.id} course={c} />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CoursePage;
